import { useEffect, useRef, useState } from "react";

export interface UseIntersectionObserverOptions {
  /**
   * The root element for intersection observation.
   * Defaults to the browser viewport if not specified.
   */
  root?: Element | null;

  /**
   * Margin around the root. Can have values similar to CSS margin property.
   * e.g. "10px 20px 30px 40px" (top, right, bottom, left).
   */
  rootMargin?: string;

  /**
   * A threshold indicating at what percentage of the target's visibility
   * the observer's callback should be executed.
   * 0 means as soon as even 1 pixel is visible, 1.0 means every pixel must be visible.
   */
  threshold?: number | number[];

  /**
   * If true, will only trigger once when the element becomes visible
   */
  triggerOnce?: boolean;
}

/**
 * Hook to detect when an element enters the viewport using Intersection Observer API
 *
 * @param options - Configuration options for the intersection observer
 * @returns A tuple containing:
 *   - ref: A ref to attach to the element you want to observe
 *   - isIntersecting: Boolean indicating if the element is currently intersecting
 *   - entry: The IntersectionObserverEntry object containing detailed intersection info
 *
 * @example
 * ```tsx
 * const [ref, isVisible] = useIntersectionObserver({
 *   threshold: 0.5,
 *   rootMargin: '100px'
 * });
 *
 * return <div ref={ref}>{isVisible ? 'Visible!' : 'Not visible'}</div>;
 * ```
 */
export const useIntersectionObserver = <T extends Element = HTMLDivElement>(
  options: UseIntersectionObserverOptions = {}
): [React.RefObject<T>, boolean, IntersectionObserverEntry | null] => {
  const {
    root = null,
    rootMargin = "0px",
    threshold = 0,
    triggerOnce = false,
  } = options;

  const ref = useRef<T>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const hasTriggered = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // If triggerOnce is true and we've already triggered, don't observe again
    if (triggerOnce && hasTriggered.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isCurrentlyIntersecting = entry.isIntersecting;

        setIsIntersecting(isCurrentlyIntersecting);
        setEntry(entry);

        if (triggerOnce && isCurrentlyIntersecting) {
          hasTriggered.current = true;
          observer.disconnect();
        }
      },
      { root, rootMargin, threshold }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [root, rootMargin, threshold, triggerOnce]);

  return [ref, isIntersecting, entry];
};
